/**
 * Guardrails — capability tiers, classification rules, refusal templates,
 * decisions log, and a fast-path test sandbox.
 *
 * All writes on this page hit the production Quill DB and affect live member
 * conversations immediately — every mutation is gated behind the shared
 * components/quill/common/QuillActionConfirm with explicit production copy
 * (see that file's header comment for why it isn't the shared
 * components/help/ActionConfirm).
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, Button, Alert, Snackbar } from '@mui/material';
import { Shield as GuardrailsIcon } from '@mui/icons-material';
import CachedIcon from '@mui/icons-material/Cached';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import QuillActionConfirm from '../common/QuillActionConfirm';
import TiersTab from './TiersTab';
import RulesTab from './RulesTab';
import TemplatesTab from './TemplatesTab';
import DecisionsTab from './DecisionsTab';
import TestSandboxTab from './TestSandboxTab';
import { colors } from '../../../theme/designTokens';
import { quillApi, QuillApiError, CapabilityTier } from '../../../services/quillAdminService';

const TAB_LABELS = ['Tiers', 'Rules', 'Refusal Templates', 'Decisions Log', 'Test Sandbox'] as const;

const GuardrailsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const [activeTab, setActiveTab] = useState(0);
  const [visited, setVisited] = useState<Set<number>>(new Set([0]));

  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadToast, setReloadToast] = useState<string | null>(null);
  const [reloadError, setReloadError] = useState<string | null>(null);

  // Tiers are fetched once at the page level too, so the Test Sandbox tab's
  // tier picker can populate without waiting on the Tiers tab ever having
  // been visited.
  const [tiers, setTiers] = useState<CapabilityTier[]>([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [tiersError, setTiersError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    quillApi.guardrails
      .getTiers()
      .then((data) => {
        if (mounted) setTiers(data);
      })
      .catch((e) => {
        if (mounted) setTiersError(e instanceof QuillApiError ? e.message : 'Failed to load tiers');
      })
      .finally(() => {
        if (mounted) setTiersLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleTabChange = (_e: React.SyntheticEvent, next: number) => {
    setActiveTab(next);
    setVisited((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
  };

  const goToSandbox = () => {
    const idx = TAB_LABELS.indexOf('Test Sandbox');
    setActiveTab(idx);
    setVisited((prev) => (prev.has(idx) ? prev : new Set(prev).add(idx)));
  };

  const handleReloadConfirm = async () => {
    setReloading(true);
    setReloadError(null);
    try {
      const res = await quillApi.guardrails.reload();
      setReloadConfirmOpen(false);
      setReloadToast(res.note || 'Reload signal sent.');
    } catch (e) {
      setReloadError(e instanceof QuillApiError ? e.message : 'Failed to signal reload');
    } finally {
      setReloading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GuardrailsIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Guardrails
              </Typography>
              <HelpTip id="quill-guardrails" />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Capability tiers, classification rules, refusal templates, and the decisions log.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<CachedIcon />}
          onClick={() => setReloadConfirmOpen(true)}
          sx={{ borderColor: colors.deepTeal[500], color: colors.deepTeal[500], height: 'fit-content' }}
        >
          Hot Reload Engine
        </Button>
      </Box>

      {reloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReloadError(null)}>
          {reloadError}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}
      >
        {TAB_LABELS.map((label) => (
          <Tab key={label} label={label} sx={{ textTransform: 'none', fontWeight: 600 }} />
        ))}
      </Tabs>

      <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>{visited.has(0) && <TiersTab />}</Box>
      <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>{visited.has(1) && <RulesTab onTrySandbox={goToSandbox} />}</Box>
      <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>{visited.has(2) && <TemplatesTab />}</Box>
      <Box sx={{ display: activeTab === 3 ? 'block' : 'none' }}>{visited.has(3) && <DecisionsTab />}</Box>
      <Box sx={{ display: activeTab === 4 ? 'block' : 'none' }}>
        {visited.has(4) && <TestSandboxTab tiers={tiers} tiersLoading={tiersLoading} tiersError={tiersError} />}
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Quill backend • {status === 'up' ? 'connected' : status}
      </Typography>

      <QuillActionConfirm
        open={reloadConfirmOpen}
        title="Hot reload guardrail engine"
        message="Signals the live Quill engine to reload guardrail configuration now."
        confirmLabel="Send reload signal"
        destructive={false}
        loading={reloading}
        pendingLabel="Applying…"
        onConfirm={handleReloadConfirm}
        onCancel={() => setReloadConfirmOpen(false)}
      />

      <Snackbar
        open={!!reloadToast}
        autoHideDuration={4000}
        onClose={() => setReloadToast(null)}
        message={reloadToast}
      />
    </Box>
  );
};

export default GuardrailsPage;
